<template>
  <div class="select-search-interactive-list">
    <div
      v-if="$slots['menu-header']"
      class="select-search-interactive-list__header"
      :class="{ 'select-search-interactive-list__header--bordered': !hasSearch }"
    >
      <slot name="menu-header" />
    </div>
    <template v-if="hasSearch">
      <tm-search
        ref="filterEl"
        class="select-search-interactive-list__search"
        :model-value="filterValue"
        :field-class="filterFieldClass"
        :placeholder="filterPlaceholder"
        :debounce="debounce"
        :size="filterSize"
        autofocus
        :aria-expanded="showDropdown"
        :aria-controls="`${instanceUid}_lb`"
        role="combobox"
        tabindex="0"
        @click.stop
        @keydown="onKeydown"
        @update:model-value="onFilter"
      />
    </template>

    <slot
      v-if="showSkeleton && loading"
      name="skeleton-list"
    >
      <tm-skeleton-select-search class="px-3" />
    </slot>

    <tm-interactive-list
      v-else-if="options?.length"
      ref="interactiveListEl"
      :combobox-id="instanceUid"
      :items="options"
      :initial-index="selectedIndex"
      :load-more-items-handler="loadMoreItemsHandler"
      :is-loading-more="isLoadingMorePage"
      overflow
      @update:model-value="onSelect"
    >
      <template v-slot="{ opt, focused }">
        <tm-sub-header-option
          v-if="opt.subheader"
          :class-name="subHeaderClass"
          :subheader-style="subheaderStyle"
          @click="focusFilter"
        >
          {{ opt.subheader }}
        </tm-sub-header-option>
        <slot
          v-else-if="opt.overrideOption"
          name="override-option"
          v-bind="{ opt, focused, focusFilter }"
        />
        <tm-default-option
          v-else
          :item-style="itemStyle"
          :class-name="itemClass"
          :focused="focused"
          :disable="opt.disabled || isDisabledOptions"
          :loading="isLoadingOptions || isLoadingFirstPage"
        >
          <slot
            name="option"
            v-bind="{ opt, filterValue }"
          >
            <tm-icon
              v-if="opt.icon"
              class="neutral--text"
              :name="opt.icon"
              left
            />
            <span class="truncate">{{ opt[optionLabel] }}</span>
          </slot>
        </tm-default-option>
      </template>
    </tm-interactive-list>
    <q-item
      v-else
      class="px-3"
    >
      <q-item-section class="text-grey">
        {{ $t('common.noResults') }}
      </q-item-section>
    </q-item>
    <slot name="menu-footer" />
  </div>
</template>

<script setup lang="ts" generic="TOption extends IBaseListOption">
import { getCurrentInstance, onMounted, ref } from '@/composition/vue/compositionApi'
import TmInteractiveList from '@/components/shared/TmInteractiveList.vue'
import TmSearch from '@/components/shared/TmSearch.vue'
import TmDefaultOption from '@/components/shared/options/TmDefaultOption.vue'
import TmSkeletonSelectSearch from '@/components/shared/skeletons/TmSkeletonSelectSearch.vue'
import TmSubHeaderOption from '@/components/shared/options/TmSubHeaderOption.vue'
import { selectOptionLabelKey } from '@/services/forms/types'
import { keyCodes } from '@/constants/keyCodes'
import type { FieldTextSize } from '@/components/forms/fields/FieldText.vue'
import { delay } from '@/utils/system'
import type { IBaseListOption } from '@/components/forms/fields/types'

withDefaults(
  defineProps<{
    selectedIndex?: number
    showDropdown?: boolean
    filterValue?: string
    filterFieldClass?: string
    filterPlaceholder?: string
    filterSize?: FieldTextSize
    debounce?: number
    showSkeleton?: boolean
    loading?: boolean
    subheaderStyle?: 'bold' | 'caption'
    subHeaderClass?: string
    itemStyle?: 'regular' | 'semi-bold'
    itemClass?: string
    options?: TOption[]
    optionLabel?: string
    isDisabledOptions?: boolean
    isLoadingOptions?: boolean
    loadMoreItemsHandler?: () => void
    isLoadingMorePage?: boolean
    isLoadingFirstPage?: boolean
    hasSearch?: boolean
  }>(),
  {
    selectedIndex: -1,
    filterFieldClass: 'px-3 pb-3',
    filterPlaceholder: 'Search',
    filterSize: 'medium',
    subheaderStyle: 'bold',
    itemStyle: 'regular',
    optionLabel: selectOptionLabelKey,
    isDisabledOptions: false,
    isLoadingOptions: false,
    hasSearch: true,
  },
)

const emit = defineEmits<{
  select: [opt: TOption]
  'update:filter': [val: string]
  'load-more-items': []
  'create-new-item': []
}>()

const instanceUid = getCurrentInstance()?.uid

const filterEl = ref()
const interactiveListEl = ref()

const onKeydown = (e: KeyboardEvent) => {
  if (e.code === keyCodes.keyEsc) {
    filterEl.value?.blur()
    return
  }
  if (e.code === keyCodes.keyEnter) {
    e.preventDefault()
  }
  if (interactiveListEl.value) {
    interactiveListEl.value.onKeydown(e)
  }
}
const onFilter = (value: string) => emit('update:filter', value)
const onSelect = (value: TOption) => emit('select', value)

const focusFilter = () => {
  filterEl.value?.focus()
}

onMounted(async () => {
  await delay(0.1)
  focusFilter()
})

defineExpose({
  onKeydown,
})
</script>

<style lang="scss" scoped>
.select-search-interactive-list {
  //--min-width: 390px; comes from props
  display: flex;
  flex-flow: column;
  overflow: hidden;
  &__new-btn-wrapper {
    display: flex;
    justify-content: flex-start;
    height: 40px;
  }

  &__header {
    padding: 0 12px 12px;

    &--bordered {
      border-bottom: 1px solid $border-color;
      margin-bottom: 8px;
    }
  }
}
</style>
